import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { getEarlyAccessVisible } from "@/lib/site-toggles.functions";

export function EmailSignup() {
  const [submitting, setSubmitting] = useState(false);
  const [visible, setVisible] = useState<boolean | null>(null);
  const { t } = useI18n();

  useEffect(() => {
    getEarlyAccessVisible()
      .then(setVisible)
      .catch(() => setVisible(true));
  }, []);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const first_name = String(formData.get("first") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();

    setSubmitting(true);
    const { supabase } = await import("@/integrations/supabase/client");
    const { error } = await supabase.from("early_access_signups").insert({
      first_name,
      email,
      locale: typeof navigator !== "undefined" ? navigator.language : null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
    setSubmitting(false);

    if (error) {
      console.error("Early access signup failed");
      toast.error(t.email.invalidEmail);
      return;
    }

    toast.success(t.email.success);
    form.reset();
  }

  if (visible === false) return null;

  return (
    <section id="updates" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-5xl px-5 md:px-8">
        <div className="film-grain relative overflow-hidden rounded-3xl border border-gold/30 bg-gradient-to-br from-night to-night-deep p-8 shadow-soft md:p-14">
          <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-glow" />
          <div className="absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-glow opacity-60" />

          <div className="relative grid gap-10 md:grid-cols-5 md:items-center">
            <div className="md:col-span-2">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-gold/40 px-3 py-1 font-mono-cinema text-[10px] text-gold">
                <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" /> {t.email.badge}
              </div>
              <h2 className="font-display text-3xl font-medium leading-[1.05] md:text-[2.6rem]">
                {t.email.title1}
                <span className="block italic text-gold">{t.email.title2}</span>
              </h2>
              <p className="mt-5 text-foreground/70">
                {t.email.body}
              </p>
              <ul className="mt-5 space-y-2 text-sm text-foreground/70">
                {t.email.bullets.map((b, i) => (
                  <li key={i} className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-gold" /> {b}</li>
                ))}
              </ul>
            </div>

            <form onSubmit={onSubmit} className="md:col-span-3">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="first" className="text-xs uppercase tracking-widest text-foreground/75">
                    {t.email.firstName}
                  </Label>
                  <Input
                    id="first"
                    name="first"
                    required
                    placeholder={t.email.firstNamePlaceholder}
                    title={t.email.required}
                    className="h-12 border-border bg-night-deep/60 text-base"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-widest text-foreground/75">
                    {t.email.email}
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder={t.email.emailPlaceholder}
                    title={t.email.invalidEmail}
                    className="h-12 border-border bg-night-deep/60 text-base"
                  />
                </div>
              </div>
              <Button type="submit" variant="hero" size="xl" disabled={submitting} className="mt-6 w-full sm:w-auto">
                {submitting ? t.email.submitting : t.email.submit}
              </Button>
              <p className="mt-3 text-xs text-foreground/75">
                {t.email.disclaimer}
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
